import { Injectable, signal } from '@angular/core';
import {
  EARN_DIMENSION_CATALOGUE,
  EarnAttribute,
  EarnAttributeStatus,
  EarnDimension,
  EarnEvent,
  NpsEvent,
  QualityState,
  QualityTimeWindow,
  deriveQualityRisk,
  npsCategory,
  sortTimeline,
} from './client-quality.model';

// ---------------------------------------------------------------------------
// Deterministic seeding helpers
// ---------------------------------------------------------------------------

function djb2(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return hash >>> 0;
}

function seedPick<T>(seed: string, options: readonly T[]): T {
  return options[djb2(seed) % options.length];
}

function isoDate(daysAgo: number, hourOffset = 10): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  d.setHours(hourOffset, 0, 0, 0);
  return d.toISOString();
}

// ---------------------------------------------------------------------------
// Seeding
// ---------------------------------------------------------------------------

const ATTR_STATUSES: EarnAttributeStatus[] = ['strong', 'strong', 'caution', 'risk', 'unknown'];

const FA_NAMES = ['Sarah Chen', 'Marcus Webb', 'Jordan Lee', 'Alex Rivera'] as const;

const NPS_VERBATIMS = [
  'Sarah was incredibly helpful and patient. She explained everything clearly.',
  'Good experience overall but the process took longer than expected.',
  'I felt heard and supported throughout the whole process.',
  'The follow-up was quick and the advice was practical.',
  'I wish the online portal was easier to navigate.',
  'Very professional team. Highly recommend.',
  'The AI assistant was surprisingly helpful and accurate.',
  undefined,
  undefined,
] as const;

const EARN_VERBATIMS = [
  'The FA demonstrated strong empathy and built trust quickly.',
  'Needs discovery was thorough — all key barriers were identified.',
  'Pathway was well-structured but client confirmation could be stronger.',
  undefined,
  undefined,
] as const;

const EARN_RECOMMENDATIONS: string[][] = [
  [
    'Strengthen client confirmation steps in Pathway Execution.',
    'Increase frequency of check-ins to reinforce Agency Building.',
    'Provide more personalized financial projections to improve Client Trust.',
  ],
  [
    'Focus on uncovering deeper motivators during Needs Discovery.',
    'Improve follow-up cadence to demonstrate Delivered Value.',
  ],
  [
    'Leverage AI summaries to reinforce Future Growth Positioning.',
    'Ensure all barriers are documented and addressed before pathway selection.',
    'Increase personalization touchpoints to improve Connection & Trust score.',
  ],
];

function seedEarnDimensions(sessionId: string, eventIdx: number): EarnDimension[] {
  return EARN_DIMENSION_CATALOGUE.map((cat, di) => {
    const dimScore = 50 + (djb2(`${sessionId}:dim:${eventIdx}:${di}`) % 51);
    const attributes: EarnAttribute[] = cat.attributes.map((attrLabel, ai) => ({
      id: `attr-${di}-${ai}`,
      label: attrLabel,
      status: seedPick(`${sessionId}:attr:${eventIdx}:${di}:${ai}`, ATTR_STATUSES),
    }));
    return {
      id: cat.id,
      label: cat.label,
      score: dimScore,
      attributes,
    };
  });
}

function seedEarnEvents(sessionId: string): EarnEvent[] {
  const count = 2 + (djb2(`${sessionId}:earn_count`) % 3); // 2–4 events
  const events: EarnEvent[] = [];
  for (let i = 0; i < count; i++) {
    const daysAgo = 10 + i * 28;
    const score = 55 + (djb2(`${sessionId}:earn_score:${i}`) % 41); // 55–95
    const faName = seedPick(`${sessionId}:earn_fa:${i}`, FA_NAMES);
    const isAi = djb2(`${sessionId}:earn_is_ai:${i}`) % 3 === 0;
    events.push({
      id: `earn-${sessionId.slice(0, 6)}-${i}`,
      type: 'earn',
      timestamp: isoDate(daysAgo, 9 + (djb2(`${sessionId}:earn_hour:${i}`) % 8)),
      caseStatus: i === 0 ? 'active' : 'closed',
      source: isAi ? 'ai' : 'fa',
      score0to100: score,
      assessedEntityType: isAi ? 'ai' : 'fa',
      assessedEntityName: isAi ? 'AIFA' : faName,
      dimensions: seedEarnDimensions(sessionId, i),
      aiSummaryBullets: EARN_RECOMMENDATIONS[i % EARN_RECOMMENDATIONS.length].slice(0, 2),
      recommendations: EARN_RECOMMENDATIONS[i % EARN_RECOMMENDATIONS.length],
      verbatim: seedPick(`${sessionId}:earn_verbatim:${i}`, EARN_VERBATIMS) ?? undefined,
    });
  }
  return events;
}

function seedNpsEvents(sessionId: string): NpsEvent[] {
  const count = 1 + (djb2(`${sessionId}:nps_count`) % 4); // 1–4 events
  const events: NpsEvent[] = [];
  const channels = ['email', 'chat', 'call_transfer', 'manual'] as const;
  for (let i = 0; i < count; i++) {
    const daysAgo = 5 + i * 25;
    const score = djb2(`${sessionId}:nps_score:${i}`) % 11; // 0–10
    events.push({
      id: `nps-${sessionId.slice(0, 6)}-${i}`,
      type: 'nps',
      timestamp: isoDate(daysAgo, 14 + (djb2(`${sessionId}:nps_hour:${i}`) % 6)),
      caseStatus: i === 0 ? 'active' : 'closed',
      source: 'system',
      score,
      category: npsCategory(score),
      channel: seedPick(`${sessionId}:nps_channel:${i}`, channels),
      verbatim: seedPick(`${sessionId}:nps_verbatim:${i}`, NPS_VERBATIMS) ?? undefined,
      aiSummaryBullets: [],
      suppressed: false,
    });
  }
  return events;
}

        // removed the following per Tom
        // score >= 9
        // ? ['Client expressed strong satisfaction with service quality.', 'Likely to recommend to peers.']
        // : score >= 7
        // ? ['Client had a generally positive experience.', 'Minor friction noted in process steps.']
        // : ['Client expressed dissatisfaction with response time.', 'Key concerns: personalization and follow-up.'],

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

interface SessionQualityEntry {
  allNps: NpsEvent[];
  allEarn: EarnEvent[];
  activeWindow: QualityTimeWindow;
}

@Injectable()
export class ClientQualityStateService {
  private readonly _raw = signal<Record<string, SessionQualityEntry>>({});
  readonly storeSnapshot = this._raw.asReadonly();

  ensureState(sessionId: string): void {
    if (this._raw()[sessionId]) return;
    this._raw.update(s => ({
      ...s,
      [sessionId]: {
        allNps: seedNpsEvents(sessionId),
        allEarn: seedEarnEvents(sessionId),
        activeWindow: '90d',
      },
    }));
  }

  getState(sessionId: string): QualityState {
    const entry = this._raw()[sessionId];
    if (!entry) return this._emptyState();
    return this._buildFromEntry(entry);
  }

  setTimeWindow(sessionId: string, window: QualityTimeWindow): void {
    this._raw.update(s => {
      const entry = s[sessionId];
      if (!entry) return s;
      return { ...s, [sessionId]: { ...entry, activeWindow: window } };
    });
  }

  private _buildFromEntry(entry: SessionQualityEntry): QualityState {
    const windowDays: Record<QualityTimeWindow, number> = {
      '30d': 30, '90d': 90, '180d': 180, '1y': 365, 'all': Infinity,
    };
    const cutoff = windowDays[entry.activeWindow];
    let filteredNps: NpsEvent[];
    let filteredEarn: EarnEvent[];
    if (cutoff === Infinity) {
      filteredNps = entry.allNps;
      filteredEarn = entry.allEarn;
    } else {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - cutoff);
      const cutoffIso = cutoffDate.toISOString();
      filteredNps = entry.allNps.filter(e => e.timestamp >= cutoffIso);
      filteredEarn = entry.allEarn.filter(e => e.timestamp >= cutoffIso);
    }
    const timeline = sortTimeline([...filteredNps, ...filteredEarn]);
    const latestNps = filteredNps.length > 0
      ? filteredNps.reduce((a, b) => a.timestamp > b.timestamp ? a : b)
      : null;
    const latestEarn = filteredEarn.length > 0
      ? filteredEarn.reduce((a, b) => a.timestamp > b.timestamp ? a : b)
      : null;

    return {
      npsEvents: filteredNps,
      earnEvents: filteredEarn,
      timeline,
      latestNps,
      latestEarn,
      qualityRisk: deriveQualityRisk(latestNps, latestEarn),
      activeTimeWindow: entry.activeWindow,
    };
  }

  private _emptyState(): QualityState {
    return {
      npsEvents: [],
      earnEvents: [],
      timeline: [],
      latestNps: null,
      latestEarn: null,
      qualityRisk: 'none',
      activeTimeWindow: '90d',
    };
  }
}
